// @flow
import {ofType, StateObservable} from 'redux-observable';
import {map, switchMap} from 'rxjs/operators';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {
  completeFirstStepsApi,
  fetchDataAnalyticsApi,
  fetchPageViewsApi,
  fetchSummaryOrdersApi,
  fetchSummaryProductCategoriesApi,
  fetchSummaryProductsApi,
  fetchSummarySalesApi,
  domainExpositionApi,
  fetchCompleteFirstStepsApi,
  fetchDomainExpositionApi,
  saveCompletedConfigApi,
  fetchCompletedConfigApi,
  fetchSummaryOrderAmountApi,
  fetchSummaryAverageTicketApi,
  fetchSummaryOrderCustomersApi,
  fetchSummaryLessStockProductsApi,
  fetchPromotionSalesApi,
  fetchCouponSalesApi,
  fetchSummaryPagesViewsApi,
  fetchSummaryMostSeenProductsApi,
  fetchProductsCountApi,
  fetchFlagLessStockProductsApi,
} from './api';

import type {ActionsObservable} from 'redux-observable';
import type {Pagination} from '../../entities';
import type {Action} from '../../store/utils';
import type {RootState} from '../index';

export type ProductData = {
  description: string,
  imageId: string,
  name: string,
  productId: string,
  salesCount: number,
}[];

export type MostSeenProductData = {
  views: string,
  imageId: string,
  name: string,
};

export type OrderData = {|
  orderState: string,
  orders: number,
  totalAmount: number,
|}[];

export type OrderCustomer = {
  amount: string,
  numOrders: string,
  fullName: string,
};

export type ProductCategoryData = {|
  categoriesCount: number,
  salesPerCategory: {name: string, amount: string}[],
|};

export type SalesData = {amount: string, count: number, interval: string}[];
export type ViewsData = {views: string, interval: string}[];

export type DomainExpositionData = {facebook?: boolean, instagram?: boolean};

export type DashboardAction = Action & {
  queryFilter?: {[key: string]: any},
  dateRange?: {
    end_date?: string,
    start_date: string,
  },
  domainExpositionRequest?: {facebook?: boolean, instagram?: boolean},
  pageViewsRequest?: {end_date: string, init_date: string},
  productLessStockRequest?: {stock: string},
  productsRequest?: {
    end_date?: string,
    init_date?: string,
    page: string,
    size: string,
  },
  pageDisplayed?: boolean,
};

export type PromotionSalesData = {
  amount: string,
  name: string,
  code?: string,
};

export type ProductLessStockData = {
  productId: string,
  imageId: string,
  stock: string,
  name: string,
};

export type DashboardState = {
  pageViews?: number,
  orderData?: OrderData,
  productCategoryData?: ProductCategoryData,
  error?: string,
  isFetchingData?: boolean,
  productsCount?: number,
  productData?: ProductData,
  mostSeenProducts?: MostSeenProductData[],
  salesData?: SalesData,
  viewsData?: ViewsData,
  isFetchingViewsData?: boolean,
  dateIni?: string,
  dateEnd?: string,
  firstStepsCompleted?: boolean,
  isCheckingIfFirstStepsCompleted?: boolean,
  domainExpositionData?: DomainExpositionData,
  completedConfig?: boolean,
  orderAmount?: string,
  averageTicket?: string,
  isFetchingCustomers?: boolean,
  customers?: OrderCustomer[],
  isFetchingLessStockProducts?: boolean,
  lessStockProducts?: ProductLessStockData[],
  isFetchingPromotionsSales?: boolean,
  isFetchingPromotionsPaginated?: boolean,
  isFetchingSummaryProducts?: boolean,
  flagLessStockProducts?: boolean,
  isFetchingFlaglessProducts?: boolean,
  isFetchingStockProductsPaginated?: boolean,
  promotionSales?: PromotionSalesData[],
  paginatedPromotions?: PromotionSalesData[],
  stockProductsPaginated?: ProductLessStockData[],
  customersPagination?: Pagination,
  customersPagination?: Pagination,
  promotionsPagination?: Pagination,
  stockProductsPagination?: Pagination,
  isFetchingCompletedConfig?: boolean,
  showWelcomeModal?: boolean,

  pageDisplayed?: boolean,
};

export class FetchDataAnalytics extends ActionCreator<DashboardAction> {}
export class FetchDataAnalyticsSuccess extends ActionCreator<DashboardAction> {}
export class FetchDataAnalyticsErrorFn extends ActionCreator<DashboardAction> {}
export class FetchPageViews extends ActionCreator<DashboardAction> {}
export class FetchPageViewsSuccess extends ActionCreator<DashboardAction> {}
export class FetchPageViewsErrorFn extends ActionCreator<DashboardAction> {}
export class FetchSummaryProducts extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductsSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductsErrorFn extends ActionCreator<DashboardAction> {}
export class FetchSummaryMostSeenProducts extends ActionCreator<DashboardAction> {}
export class FetchSummaryMostSeenProductsSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryMostSeenProductsErrorFn extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrders extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrdersSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrdersErrorFn extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductCategories extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductCategoriesSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductCategoriesErrorFn extends ActionCreator<DashboardAction> {}
export class FetchSummarySales extends ActionCreator<DashboardAction> {}
export class FetchSummarySalesSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummarySalesErrorFn extends ActionCreator<DashboardAction> {}
export class CompleteFirstSteps extends ActionCreator<DashboardAction> {}
export class CompleteFirstStepsSuccess extends ActionCreator<DashboardAction> {}
export class CompleteFirstStepsErrorFn extends ActionCreator<DashboardAction> {}
export class DomainExposition extends ActionCreator<DashboardAction> {}
export class DomainExpositionSuccess extends ActionCreator<DashboardAction> {}
export class DomainExpositionErrorFn extends ActionCreator<DashboardAction> {}
export class FetchCompleteFirstSteps extends ActionCreator<DashboardAction> {}
export class FetchCompleteFirstStepsSuccess extends ActionCreator<DashboardAction> {}
export class FetchCompleteFirstStepsErrorFn extends ActionCreator<DashboardAction> {}
export class FetchDomainExposition extends ActionCreator<DashboardAction> {}
export class FetchDomainExpositionSuccess extends ActionCreator<DashboardAction> {}
export class FetchDomainExpositionErrorFn extends ActionCreator<DashboardAction> {}
export class SaveCompletedConfig extends ActionCreator<DashboardAction> {}
export class SaveCompletedConfigSuccess extends ActionCreator<DashboardAction> {}
export class SaveCompletedConfigErrorFn extends ActionCreator<DashboardAction> {}
export class FetchCompletedConfig extends ActionCreator<DashboardAction> {}
export class FetchCompletedConfigSuccess extends ActionCreator<DashboardAction> {}
export class FetchCompletedConfigErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryProductsCount extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductsCountSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryProductsCountErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryOrderAmount extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrderAmountSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrderAmountErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryAverageTicket extends ActionCreator<DashboardAction> {}
export class FetchSummaryAverageTicketSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryAverageTicketErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryOrderCustomers extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrderCustomersSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryOrderCustomersErrorFn extends ActionCreator<DashboardAction> {}

export class FetchFlagLessStockProducts extends ActionCreator<DashboardAction> {}
export class FetchFlagLessStockProductsSuccess extends ActionCreator<DashboardAction> {}
export class FetchFlagLessStockProductsErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryLessStockProducts extends ActionCreator<DashboardAction> {}
export class FetchSummaryLessStockProductsSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryLessStockProductsErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryPromotionSales extends ActionCreator<DashboardAction> {}
export class FetchSummaryPromotionSalesSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryPromotionSalesErrorFn extends ActionCreator<DashboardAction> {}

export class FetchSummaryPagesView extends ActionCreator<DashboardAction> {}
export class FetchSummaryPagesViewSuccess extends ActionCreator<DashboardAction> {}
export class FetchSummaryPagesViewErrorFn extends ActionCreator<DashboardAction> {}

export class SetPageDisplayed extends ActionCreator<DashboardAction> {}

export const actions = {
  fetchDataAnalytics: () => new FetchDataAnalytics(),
  fetchPageViews: (payload: DashboardAction) => new FetchPageViews(payload),
  fetchSummaryPagesView: () => new FetchSummaryPagesView(),
  fetchSummaryProductsCount: () => new FetchSummaryProductsCount(),
  fetchSummaryProducts: (payload: DashboardAction) =>
    new FetchSummaryProducts(payload),
  fetchSummaryMostSeenProducts: (payload: DashboardAction) =>
    new FetchSummaryMostSeenProducts(payload),
  fetchSummaryOrders: (payload: DashboardAction) =>
    new FetchSummaryOrders(payload),
  fetchSummaryProductCategories: (payload: DashboardAction) =>
    new FetchSummaryProductCategories(payload),
  fetchSummarySales: () => new FetchSummarySales(),
  completeFirstSteps: () => new CompleteFirstSteps(),
  domainExposition: (payload: DashboardAction) => new DomainExposition(payload),
  fetchCompleteFirstSteps: () => new FetchCompleteFirstSteps(),
  fetchDomainExposition: () => new FetchDomainExposition(),
  saveCompletedConfig: (payload: DashboardAction) =>
    new SaveCompletedConfig(payload),
  fetchCompletedConfig: () => new FetchCompletedConfig(),
  fetchSummaryOrderAmount: (payload: DashboardAction) =>
    new FetchSummaryOrderAmount(payload),
  fetchSummaryAverageTicket: (payload: DashboardAction) =>
    new FetchSummaryAverageTicket(payload),
  fetchSummaryOrderCustomers: (payload: DashboardAction) =>
    new FetchSummaryOrderCustomers(payload),
  fetchPromotionSales: (payload: DashboardAction) =>
    new FetchSummaryPromotionSales(payload),
  fetchLessStockProducts: (payload: DashboardAction) =>
    new FetchSummaryLessStockProducts(payload),
  fetchFlagLessStockProducts: () => new FetchFlagLessStockProducts(),
  setPageDisplayed: (value: boolean) =>
    new SetPageDisplayed({
      pageDisplayed: value,
    }),
};

export const initialState = {};

export default createReducer<DashboardState>(initialState, {
  [FetchDataAnalytics.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchDataAnalyticsSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    orderData: action.data.orderData.orderStatesData,
    productCategoryData: action.data.productCategoryData,
    productData: action.data.productData,
    dateIni: action.data.dateIni,
    dateEnd: action.data.dateEnd,
  }),
  [FetchDataAnalyticsErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [FetchPageViews.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchPageViewsSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    pageViews: action.data.views,
  }),
  [FetchPageViewsErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [FetchSummaryProductsCountSuccess.type]: (state, action) => ({
    ...state,
    productsCount: action.data,
  }),
  [FetchSummaryProductsCountErrorFn.type]: (state, action) => ({
    ...state,
    productsCount: undefined,
  }),
  [FetchSummaryProducts.type]: state => ({
    ...state,
    isFetchingSummaryProducts: true,
    // productData: undefined,
    // mostSeenProducts: undefined,
  }),
  [FetchSummaryProductsSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingSummaryProducts: undefined,
    productData: action.data,
    mostSeenProducts: [],
    productDataPagination: action.pagination,
  }),
  [FetchSummaryProductsErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingSummaryProducts: undefined,
    error: action.error,
  }),
  [FetchSummaryMostSeenProductsSuccess.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingSummaryProducts: undefined,
    mostSeenProducts: action.data,
    productData: [],
    mostSeenProductsPagination: action.pagination,
  }),
  [FetchSummaryMostSeenProductsErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingSummaryProducts: undefined,
    error: action.error,
  }),
  [FetchSummaryOrders.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchSummaryOrdersSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    orderData: action.data.orderStates,
    // dateIni: action.data.dateIni,
    // dateEnd: action.data.dateEnd,
  }),
  [FetchSummaryOrdersErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [FetchSummaryProductCategories.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchSummaryProductCategoriesSuccess.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingData: undefined,
    productCategoryData: action.data,
    // dateIni: action.data.dateIni,
    // dateEnd: action.data.dateEnd,
  }),
  [FetchSummaryProductCategoriesErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [FetchSummarySales.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchSummarySalesSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    salesData: action.data.intervals,
    // dateIni: action.data.dateIni,
    // dateEnd: action.data.dateEnd,
  }),
  [FetchSummarySalesErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [FetchSummaryOrderAmountSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    orderAmount: action.data,
  }),
  [FetchSummaryOrderAmountErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    orderAmount: undefined,
  }),
  [FetchSummaryAverageTicketSuccess.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    averageTicket: action.data,
  }),
  [FetchSummaryAverageTicketErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    averageTicket: undefined,
  }),
  [FetchSummaryOrderCustomers.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingCustomers: true,
  }),
  [FetchSummaryOrderCustomersSuccess.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingCustomers: undefined,
    customers: action.data,
    customersPagination: action.pagination,
  }),
  [FetchSummaryOrderCustomersErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingCustomers: undefined,
    customers: [],
  }),
  [FetchSummaryPromotionSales.type]: (state, action: DashboardAction) => {
    const paginated = action.queryFilter
      ? action.queryFilter.paginated
      : undefined;
    if (paginated) {
      return {
        ...state,
        isFetchingPromotionsPaginated: true,
      };
    }
    return {
      ...state,
      isFetchingPromotionsSales: true,
    };
  },
  [FetchSummaryPromotionSalesSuccess.type]: (
    state,
    action: DashboardAction
  ) => {
    let newState = {promotionSales: action.data};
    if (action.paginated) {
      let paginatedPromotions = action.data;
      if (action.addPage) {
        const oldP = state.paginatedPromotions ? state.paginatedPromotions : [];
        const newP = action.data;
        paginatedPromotions = [...oldP, ...newP];
      }
      newState = {
        promotionsPagination: action.pagination,
        paginatedPromotions,
      };
    }
    return {
      ...state,
      ...newState,
      isFetchingPromotionsSales: undefined,
      isFetchingPromotionsPaginated: undefined,
    };
  },
  [FetchSummaryPromotionSalesErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingPromotionsSales: undefined,
    isFetchingPromotionsPaginated: undefined,
    promotionSales: [],
    paginatedPromotions: [],
  }),
  [FetchSummaryLessStockProducts.type]: (state, action: DashboardAction) => {
    const paginated = action.queryFilter
      ? action.queryFilter.paginated
      : undefined;
    if (paginated) {
      return {
        ...state,
        isFetchingStockProductsPaginated: true,
      };
    }
    return {
      ...state,
      isFetchingLessStockProducts: true,
    };
  },
  [FetchSummaryLessStockProductsSuccess.type]: (
    state,
    action: DashboardAction
  ) => {
    let newState = {lessStockProducts: action.data};
    if (action.paginated) {
      let stockProductsPaginated = action.data;
      if (action.addPage) {
        const oldStockPPag = state.stockProductsPaginated
          ? state.stockProductsPaginated
          : [];
        const newData = action.data;
        stockProductsPaginated = [...oldStockPPag, ...newData];
      }
      newState = {
        stockProductsPagination: action.pagination,
        stockProductsPaginated,
      };
    }
    return {
      ...state,
      ...newState,
      isFetchingLessStockProducts: undefined,
      isFetchingStockProductsPaginated: undefined,
    };
  },
  [FetchSummaryLessStockProductsErrorFn.type]: (
    state,
    action: DashboardAction
  ) => ({
    ...state,
    isFetchingStockProductsPaginated: undefined,
    isFetchingLessStockProducts: undefined,
    lessStockProducts: [],
    stockProductsPaginated: [],
  }),
  [FetchFlagLessStockProducts.type]: state => ({
    ...state,
    isFetchingFlaglessProducts: true,
  }),
  [FetchFlagLessStockProductsSuccess.type]: (state, action) => ({
    ...state,
    isFetchingFlaglessProducts: undefined,
    flagLessStockProducts: action.data,
  }),
  [FetchFlagLessStockProductsErrorFn.type]: (state, action) => ({
    ...state,
    isFetchingFlaglessProducts: undefined,
    flagLessStockProducts: false,
  }),
  [FetchSummaryPagesView.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingViewsData: true,
  }),
  [FetchSummaryPagesViewSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingViewsData: undefined,
    viewsData: action.data.intervals,
  }),
  [FetchSummaryPagesViewErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingViewsData: undefined,
    error: action.error,
  }),
  [CompleteFirstSteps.type]: state => ({
    ...state,
    // isFetchingData: true,
  }),
  [CompleteFirstStepsSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    // isFetchingData: undefined,
    // pageViews: action.data.views,
  }),
  [CompleteFirstStepsErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    // isFetchingData: undefined,
    error: action.error,
  }),
  [DomainExposition.type]: state => ({
    ...state,
    // isFetchingData: true,
  }),
  [DomainExpositionSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    // isFetchingData: undefined,
    // pageViews: action.data.views,
  }),
  [DomainExpositionErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    // isFetchingData: undefined,
    error: action.error,
  }),
  [FetchCompleteFirstSteps.type]: state => ({
    ...state,
    isCheckingIfFirstStepsCompleted: true,
  }),
  [FetchCompleteFirstStepsSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isCheckingIfFirstStepsCompleted: false,
    firstStepsCompleted: action.data.completed,
  }),
  [FetchCompleteFirstStepsErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isCheckingIfFirstStepsCompleted: false,
    firstStepsCompleted: false,
    error: action.error,
  }),
  [FetchDomainExposition.type]: state => ({
    ...state,
    isFetchingData: true,
  }),
  [FetchDomainExpositionSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    domainExpositionData: action.data,
  }),
  [FetchDomainExpositionErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingData: undefined,
    error: action.error,
  }),
  [SaveCompletedConfig.type]: state => ({
    ...state,
  }),
  [SaveCompletedConfigSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    completedConfig: action.data.completed ? action.data.completed : false,
    showWelcomeModal: true,
  }),
  [SaveCompletedConfigErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    // error: action.error,
  }),
  [FetchCompletedConfig.type]: state => ({
    ...state,
    isFetchingCompletedConfig: true,
    completedConfig: undefined,
  }),
  [FetchCompletedConfigSuccess.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingCompletedConfig: undefined,
    completedConfig: action.data.completed ? action.data.completed : false,
  }),
  [FetchCompletedConfigErrorFn.type]: (state, action: DashboardAction) => ({
    ...state,
    isFetchingCompletedConfig: undefined,
    // error: action.error,
    completedConfig: false,
  }),
  [SetPageDisplayed.type]: (state, action: DashboardAction) => ({
    ...state,
    pageDisplayed: action.pageDisplayed,
  }),
});

export function fetchDataAnalyticsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchDataAnalytics.type),
    switchMap(() => {
      // const {store} = state$.value.store;
      // let name: string = store ? store.storeTier.name : '';

      let api$: ActionsObservable;

      // if (name >= 'TIER_3') {
      //   api$ = fetchDataAnalyticsApi({
      //     end_date: dateEnd.toISOString(),
      //     init_date: dateIni.toISOString(),
      //   });
      // } else {
      api$ = fetchDataAnalyticsApi();
      // }

      return api$.pipe(
        map(
          response =>
            new FetchDataAnalyticsSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchDataAnalyticsErrorFn)
      );
    })
  );
}

export function fetchPageViewsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchPageViews.type),
    switchMap(({dateRange}: DashboardAction) => {
      let qf = {};
      if (dateRange) {
        qf = dateRange;
      }
      return fetchPageViewsApi(qf).pipe(
        map(
          response =>
            new FetchPageViewsSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchPageViewsErrorFn)
      );
    })
  );
}

export function fetchSummaryProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryProducts.type),
    switchMap(({productsRequest}: DashboardAction) => {
      let qf = productsRequest ? productsRequest : {};
      const {type, ...queryFilter} = qf;
      if (type === 'most-seen') {
        return fetchSummaryMostSeenProductsApi(queryFilter).pipe(
          map(
            ({data, pagination}) =>
              new FetchSummaryMostSeenProductsSuccess({
                data,
                pagination,
              })
          ),
          buildCatchError(FetchSummaryMostSeenProductsErrorFn)
        );
      }
      return fetchSummaryProductsApi(queryFilter).pipe(
        map(
          ({data, pagination}) =>
            new FetchSummaryProductsSuccess({
              data,
              pagination,
            })
        ),
        buildCatchError(FetchSummaryProductsErrorFn)
      );
    })
  );
}

export function fetchSummaryOrdersEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryOrders.type),
    switchMap(({dateRange}: DashboardAction) => {
      // const date = new Date();
      // const month = date.getMonth() % 12;

      // let year = date.getUTCFullYear();
      // year = month === 12 ? year - 1 : year;

      // const dateIni = new Date(Date.UTC(year, month, 1));
      // const dateEnd = new Date(Date.UTC(year, month + 1, 0));
      return fetchSummaryOrdersApi(dateRange).pipe(
        map(
          response =>
            new FetchSummaryOrdersSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchSummaryOrdersErrorFn)
      );
    })
  );
}

export function fetchSummaryProductCategoriesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryProductCategories.type),
    switchMap(({dateRange}: DashboardAction) => {
      let qf = {};
      if (dateRange) {
        qf = {
          init_date: dateRange.start_date,
          end_date: dateRange.end_date,
        };
      }
      return fetchSummaryProductCategoriesApi(qf).pipe(
        map(
          response =>
            new FetchSummaryProductCategoriesSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchSummaryProductCategoriesErrorFn)
      );
    })
  );
}

export function fetchSummaryPageViewsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryPagesView.type),
    switchMap(() => {
      return fetchSummaryPagesViewsApi().pipe(
        map(
          response =>
            new FetchSummaryPagesViewSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchSummaryPagesViewErrorFn)
      );
    })
  );
}

export function fetchSummarySalesEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummarySales.type),
    switchMap(({dateRange}: DashboardAction) => {
      return fetchSummarySalesApi().pipe(
        map(
          response =>
            new FetchSummarySalesSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchSummarySalesErrorFn)
      );
    })
  );
}

export function fetchSummaryOrderAmountEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryOrderAmount.type),
    switchMap(({dateRange}: DashboardAction) => {
      let qf = {};
      const states = [
        'PENDING',
        'CONFIRMED',
        'PAID',
        'ON_THE_WAY',
        'DELIVERED',
      ];
      if (dateRange) {
        qf = {
          init_date: dateRange.start_date,
          end_date: dateRange.end_date,
          states: states.join(','),
        };
      }

      return fetchSummaryOrderAmountApi(qf).pipe(
        map(response => {
          return new FetchSummaryOrderAmountSuccess({
            data: response.amount,
          });
        }),
        buildCatchError(FetchSummaryOrderAmountErrorFn)
      );
    })
  );
}

export function fetchSummaryOrderCustomersEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryOrderCustomers.type),
    switchMap(({queryFilter}: DashboardAction) => {
      return fetchSummaryOrderCustomersApi(queryFilter).pipe(
        map(({data, pagination}) => {
          return new FetchSummaryOrderCustomersSuccess({
            data,
            pagination,
          });
        }),
        buildCatchError(FetchSummaryOrderCustomersErrorFn)
      );
    })
  );
}

export function fetchFlagLessStockProductsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchFlagLessStockProducts.type),
    switchMap(() => {
      return fetchFlagLessStockProductsApi().pipe(
        map(({hasStock}) => {
          return new FetchFlagLessStockProductsSuccess({
            data: !!hasStock,
          });
        }),
        buildCatchError(FetchFlagLessStockProductsErrorFn)
      );
    })
  );
}

export function fetchSummaryLessStockProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchSummaryLessStockProducts.type),
    switchMap(({queryFilter}: DashboardAction) => {
      const {paginated, reset, ...qf} = queryFilter;
      if (paginated) {
        const page =
          state$.value.dashboard &&
          state$.value.dashboard.stockProductsPagination
            ? +state$.value.dashboard.stockProductsPagination.page + 1
            : 0;

        qf.page = reset ? 0 : page;
        qf.size = 10;
      } else {
        qf.page = 0;
        qf.size = 6;
      }
      return fetchSummaryLessStockProductsApi(qf).pipe(
        map(({data, pagination}) => {
          return new FetchSummaryLessStockProductsSuccess({
            data,
            pagination,
            paginated,
            addPage: !reset,
          });
        }),
        buildCatchError(FetchSummaryLessStockProductsErrorFn)
      );
    })
  );
}

export function fetchProductsCountEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryProductsCount.type),
    switchMap(() => {
      return fetchProductsCountApi().pipe(
        map(({productsCount}) => {
          return new FetchSummaryProductsCountSuccess({
            data: productsCount,
          });
        }),
        buildCatchError(FetchSummaryProductsCountErrorFn)
      );
    })
  );
}

export function fetchSummaryPromotionSalesEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchSummaryPromotionSales.type),
    switchMap(({queryFilter}: DashboardAction) => {
      const {type, paginated, reset, ...otherQueries} = queryFilter;
      let qf = {...otherQueries};
      let api$;
      if (paginated) {
        const page =
          state$.value.dashboard &&
          state$.value.dashboard.promotionsPagination &&
          typeof state$.value.dashboard.promotionsPagination.page === 'number'
            ? state$.value.dashboard.promotionsPagination.page + 1
            : 0;
        qf.page = reset ? 0 : page;
        qf.size = 10;
      } else {
        qf.page = 0;
        qf.size = 6;
      }
      if (type === 'coupon') {
        api$ = fetchCouponSalesApi(qf);
      } else api$ = fetchPromotionSalesApi(qf);
      return api$.pipe(
        map(({data, pagination}) => {
          return new FetchSummaryPromotionSalesSuccess({
            data,
            pagination,
            paginated,
            paginatedPromotions: data,
            addPage: !reset,
          });
        }),
        buildCatchError(FetchSummaryPromotionSalesErrorFn)
      );
    })
  );
}

export function fetchSummaryAverageTicketEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchSummaryAverageTicket.type),
    switchMap(({dateRange}: DashboardAction) => {
      const states = [
        'PENDING',
        'CONFIRMED',
        'PAID',
        'ON_THE_WAY',
        'DELIVERED',
      ];
      const dr = {
        ...dateRange,
        init_date: dateRange.start_date,
        states: states.join(','),
      };
      delete dr.start_date;
      return fetchSummaryAverageTicketApi(dr).pipe(
        map(response => {
          return new FetchSummaryAverageTicketSuccess({
            data: response.amount,
          });
        }),
        buildCatchError(FetchSummaryAverageTicketErrorFn)
      );
    })
  );
}

export function completeFirstStepsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(CompleteFirstSteps.type),
    switchMap(() => {
      return completeFirstStepsApi().pipe(
        map(
          response =>
            new CompleteFirstStepsSuccess({
              data: response,
            })
        ),
        buildCatchError(CompleteFirstStepsErrorFn)
      );
    })
  );
}

export function fetchCompleteFirstStepsEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchCompleteFirstSteps.type, CompleteFirstStepsSuccess.type),
    switchMap(() => {
      return fetchCompleteFirstStepsApi().pipe(
        map(
          response =>
            new FetchCompleteFirstStepsSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchCompleteFirstStepsErrorFn)
      );
    })
  );
}

export function domainExpositionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(DomainExposition.type),
    switchMap(({domainExpositionRequest}: DashboardAction) => {
      return domainExpositionApi(domainExpositionRequest).pipe(
        map(
          response =>
            new DomainExpositionSuccess({
              data: response,
            })
        ),
        buildCatchError(DomainExpositionErrorFn)
      );
    })
  );
}

export function fetchDomainExpositionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchDomainExposition.type, DomainExpositionSuccess.type),
    switchMap(() => {
      return fetchDomainExpositionApi().pipe(
        map(
          response =>
            new FetchDomainExpositionSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchDomainExpositionErrorFn)
      );
    })
  );
}

export function saveCompletedConfigEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SaveCompletedConfig.type),
    switchMap(({completedConfigRequest}: DashboardAction) => {
      return saveCompletedConfigApi(completedConfigRequest).pipe(
        map(
          response =>
            new SaveCompletedConfigSuccess({
              data: response,
            })
        ),
        buildCatchError(SaveCompletedConfigErrorFn)
      );
    })
  );
}

export function fetchCompletedConfigEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(FetchCompletedConfig.type),
    switchMap(() => {
      return fetchCompletedConfigApi().pipe(
        map(
          response =>
            new FetchCompletedConfigSuccess({
              data: response,
            })
        ),
        buildCatchError(FetchCompletedConfigErrorFn)
      );
    })
  );
}
